import React, { useEffect, useMemo, useState } from "react";
import { HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlineMagnifyingGlass, HiOutlineXMark, HiOutlineSquares2X2, HiOutlineCheckCircle, HiOutlineClipboardDocumentList } from 'react-icons/hi2';
import { authService } from "../../services";
import masterDataService from "../../services/masterDataService";
import SharedModal from '../../components/common/SharedModal';
import machineService from '../../services/machineService';
import subassemblyService from '../../services/subassemblyService';
import plantService from '../../services/plantService';
import lineService from '../../services/lineService';
import PageLoading from "../../components/common/PageLoading";
import "./PlannedMaintenanceMasters.css";

const PlannedMaintenanceMasters = () => {
  const tenantId = authService.getTenantId();
  const [selectedMaster, setSelectedMaster] = useState("maintenancePlans");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const masterConfigs = useMemo(
    () => ({
      maintenancePlans: {
        label: "Maintenance Plans",
        columns: [
          { key: "maintenancePlanId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "planCode", label: "Code" },
          { key: "planName", label: "Name" },
          { key: "frequencyType", label: "Type" },
          { key: "frequencyValue", label: "Value" },
          { key: "frequencyUnit", label: "Unit" },
          {
            key: "isActive",
            label: "Active",
            render: (v) => (v ? "Yes" : "No"),
          },
        ],
        fields: [
          {
            name: "planCode",
            label: "Plan Code",
            type: "text",
            required: true,
            placeholder: "Enter plan code"
          },
          {
            name: "planName",
            label: "Plan Name",
            type: "text",
            required: true,
            placeholder: "Enter plan name"
          },
          { 
            name: "description", 
            label: "Description", 
            type: "textarea",
            placeholder: "Enter description (optional)"
          },
          {
            name: "assetCategoryId",
            label: "Asset Category Id",
            type: "number",
          },
          {
            name: "frequencyType",
            label: "Frequency Type",
            type: "text",
            placeholder: "Time / Meter / Condition / Hybrid",
          },
          { name: "frequencyValue", label: "Frequency Value", type: "number" },
          {
            name: "frequencyUnit",
            label: "Frequency Unit",
            type: "text",
            placeholder: "Days / Hours / Cycles",
          },
          {
            name: "estimatedDurationMinutes",
            label: "Est. Duration (min)",
            type: "number",
          },
          {
            name: "estimatedLaborHours",
            label: "Est. Labor (hrs)",
            type: "number",
            step: "0.1",
          },
          {
            name: "requiredSkillId",
            label: "Required Skill Id",
            type: "number",
          },
          {
            name: "defaultPriorityId",
            label: "Default Priority Id",
            type: "number",
          },
          {
            name: "defaultSparePartsJson",
            label: "Default Spare Parts (JSON)",
            type: "textarea",
          },
          { name: "isActive", label: "Active", type: "checkbox" },
        ],
        listFn: (tid) => masterDataService.getMaintenancePlans(tid),
        createFn: masterDataService.createMaintenancePlan,
        updateFn: masterDataService.updateMaintenancePlan,
        deleteFn: masterDataService.deleteMaintenancePlan,
        idKey: "maintenancePlanId",
      },
      maintenancePlanTasks: {
        label: "Maintenance Plan Tasks",
        columns: [
          { key: "taskId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "maintenancePlanId", label: "Plan Id" },
          { key: "sequenceNo", label: "Seq" },
          { key: "taskDescription", label: "Task" },
          {
            key: "isMandatory",
            label: "Mandatory",
            render: (v) => (v ? "Yes" : "No"),
          },
        ],
        fields: [
          {
            name: "maintenancePlanId",
            label: "Plan Id",
            type: "number",
            required: true,
          },
          {
            name: "sequenceNo",
            label: "Sequence",
            type: "number",
            required: true,
          },
          {
            name: "taskDescription",
            label: "Task Description",
            type: "textarea",
            required: true,
          },
          {
            name: "expectedResult",
            label: "Expected Result",
            type: "textarea",
          },
          {
            name: "inspectionFieldCode",
            label: "Inspection Field Code",
            type: "text",
          },
          { name: "isMandatory", label: "Mandatory", type: "checkbox" },
          { name: "measurementUnit", label: "Measurement Unit", type: "text" },
          {
            name: "targetMin",
            label: "Target Min",
            type: "number",
            step: "0.01",
          },
          {
            name: "targetMax",
            label: "Target Max",
            type: "number",
            step: "0.01",
          },
        ],
        listFn: (tid) => masterDataService.getMaintenancePlanTasks(tid),
        createFn: masterDataService.createMaintenancePlanTask,
        updateFn: masterDataService.updateMaintenancePlanTask,
        deleteFn: masterDataService.deleteMaintenancePlanTask,
        idKey: "taskId",
      },
      pmSchedules: {
        label: "PM Schedules",
        columns: [
          { key: "pMScheduleId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "maintenancePlanId", label: "Plan Id" },
          { key: "assetId", label: "Asset Id" },
          {
            key: "nextDueDate",
            label: "Next Due",
            render: (v) => (v ? new Date(v).toLocaleDateString() : "-"),
          },
          {
            key: "scheduleMode",
            label: "Mode",
            render: (v) => {
              if (!v) return "-";
              if (typeof v === "object")
                return v?.ScheduleMode || v?.scheduleMode || JSON.stringify(v);
              return v;
            },
          },
          { key: "active", label: "Active", render: (v) => (v ? "Yes" : "No") },
        ],
        fields: [
          {
            name: "maintenancePlanId",
            label: "Plan Id",
            type: "number",
            required: true,
          },
          {
            name: "assetId",
            label: "Asset Id",
            type: "number",
            required: true,
          },
          {
            name: "startDate",
            label: "Start Date",
            type: "date",
            required: true,
          },
          { name: "endDate", label: "End Date", type: "date" },
          {
            name: "nextDueDate",
            label: "Next Due Date",
            type: "date",
            required: true,
          },
          { name: "scheduleMode", label: "Schedule Mode", type: "text" },
          {
            name: "meterThreshold",
            label: "Meter Threshold",
            type: "number",
            step: "0.01",
          },
          {
            name: "triggerSensorTagId",
            label: "Trigger Sensor Tag Id",
            type: "number",
          },
          { name: "assignedTeamId", label: "Assigned Team Id", type: "number" },
          {
            name: "notificationsConfigJson",
            label: "Notifications Config (JSON)",
            type: "textarea",
          },
          {
            name: "autoCreateWorkOrder",
            label: "Auto Create Work Order",
            type: "checkbox",
          },
          { name: "active", label: "Active", type: "checkbox" },
        ],
        listFn: (tid) => masterDataService.getPMSchedules(tid),
        createFn: masterDataService.createPMSchedule,
        updateFn: masterDataService.updatePMSchedule,
        deleteFn: masterDataService.deletePMSchedule,
        idKey: "pMScheduleId",
      },
      maintenanceWindows: {
        label: "Maintenance Windows",
        columns: [
          { key: "windowId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "windowCode", label: "Code" },
          { key: "windowName", label: "Name" },
          {
            key: "isShutdownWindow",
            label: "Shutdown",
            render: (v) => (v ? "Yes" : "No"),
          },
        ],
        fields: [
          {
            name: "windowCode",
            label: "Window Code",
            type: "text",
            required: true,
          },
          {
            name: "windowName",
            label: "Window Name",
            type: "text",
            required: true,
          },
          {
            name: "startTime",
            label: "Start Time",
            type: "time",
            required: true,
          },
          { name: "endTime", label: "End Time", type: "time", required: true },
          {
            name: "isShutdownWindow",
            label: "Shutdown Window",
            type: "checkbox",
          },
        ],
        listFn: (tid) => masterDataService.getMaintenanceWindows(tid),
        createFn: masterDataService.createMaintenanceWindow,
        updateFn: masterDataService.updateMaintenanceWindow,
        deleteFn: masterDataService.deleteMaintenanceWindow,
        idKey: "windowId",
      },
      technicianSkills: {
        label: "Technician Skills",
        columns: [
          { key: "skillId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "skillName", label: "Name" },
          { key: "description", label: "Description" },
        ],
        fields: [
          {
            name: "skillName",
            label: "Skill Name",
            type: "text",
            required: true,
          },
          { name: "description", label: "Description", type: "textarea" },
        ],
        listFn: (tid) => masterDataService.getTechnicianSkills(tid),
        createFn: masterDataService.createTechnicianSkill,
        updateFn: masterDataService.updateTechnicianSkill,
        deleteFn: masterDataService.deleteTechnicianSkill,
        idKey: "skillId",
      },
      technicianTeams: {
        label: "Technician Teams",
        columns: [
          { key: "teamId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "teamName", label: "Name" },
          { key: "leadUserId", label: "Lead User Id" },
        ],
        fields: [
          {
            name: "teamName",
            label: "Team Name",
            type: "text",
            required: true,
          },
          { name: "leadUserId", label: "Lead User Id", type: "number" },
          {
            name: "memberUserIdsJson",
            label: "Member User Ids (JSON)",
            type: "textarea",
          },
          { name: "defaultShiftId", label: "Default Shift Id", type: "number" },
        ],
        listFn: (tid) => masterDataService.getTechnicianTeams(tid),
        createFn: masterDataService.createTechnicianTeam,
        updateFn: masterDataService.updateTechnicianTeam,
        deleteFn: masterDataService.deleteTechnicianTeam,
        idKey: "teamId",
      },
      tools: {
        label: "Tools",
        columns: [
          { key: "toolId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "toolCode", label: "Code" },
          { key: "toolName", label: "Name" },
          {
            key: "calibrationRequired",
            label: "Calibration",
            render: (v) => (v ? "Yes" : "No"),
          },
        ],
        fields: [
          {
            name: "toolName",
            label: "Tool Name",
            type: "text",
            required: true,
          },
          { name: "toolCode", label: "Tool Code", type: "text" },
          {
            name: "calibrationRequired",
            label: "Calibration Required",
            type: "checkbox",
          },
          {
            name: "calibrationFreqDays",
            label: "Calibration Freq (days)",
            type: "number",
          },
          { name: "locationId", label: "Location Id", type: "number" },
        ],
        listFn: (tid) => masterDataService.getTools(tid),
        createFn: masterDataService.createTool,
        updateFn: masterDataService.updateTool,
        deleteFn: masterDataService.deleteTool,
        idKey: "toolId",
      },
      ppeSafety: {
        label: "PPE (Safety)",
        columns: [
          { key: "ppeId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "ppeName", label: "Name" },
          { key: "description", label: "Description" },
        ],
        fields: [
          { name: "ppeName", label: "PPE Name", type: "text", required: true },
          { name: "description", label: "Description", type: "textarea" },
        ],
        listFn: (tid) => masterDataService.getPpeSafety(tid),
        createFn: masterDataService.createPpeSafety,
        updateFn: masterDataService.updatePpeSafety,
        deleteFn: masterDataService.deletePpeSafety,
        idKey: "ppeId",
      },
      sensorTags: {
        label: "Sensor Tags",
        columns: [
          { key: "tagId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "machineId", label: "Machine Id" },
          { key: "tagName", label: "Tag Name" },
          { key: "dataType", label: "Data Type" },
        ],
        fields: [
          {
            name: "machineId",
            label: "Machine Id",
            type: "number",
            required: true,
          },
          { name: "tagName", label: "Tag Name", type: "text", required: true },
          { name: "opcAddress", label: "OPC Address", type: "text" },
          { name: "mqttTopic", label: "MQTT Topic", type: "text" },
          { name: "dataType", label: "Data Type", type: "text" },
          {
            name: "pollingIntervalSec",
            label: "Polling Interval (sec)",
            type: "number",
          },
          { name: "description", label: "Description", type: "textarea" },
        ],
        listFn: (tid) => masterDataService.getSensorTags(tid),
        createFn: masterDataService.createSensorTag,
        updateFn: masterDataService.updateSensorTag,
        deleteFn: masterDataService.deleteSensorTag,
        idKey: "tagId",
      },
      sensorThresholds: {
        label: "Sensor Thresholds",
        columns: [
          { key: "ruleId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "tagId", label: "Tag Id" },
          { key: "conditionOperator", label: "Op" },
          { key: "thresholdValue", label: "Threshold" },
          { key: "action", label: "Action" },
        ],
        fields: [
          { name: "tagId", label: "Tag Id", type: "number", required: true },
          {
            name: "conditionOperator",
            label: "Condition Operator",
            type: "text",
            required: true,
          },
          {
            name: "thresholdValue",
            label: "Threshold Value",
            type: "number",
            required: true,
          },
          {
            name: "consecutiveCount",
            label: "Consecutive Count",
            type: "number",
          },
          { name: "holdTimeSec", label: "Hold Time (sec)", type: "number" },
          { name: "action", label: "Action", type: "text" },
          { name: "severity", label: "Severity", type: "text" },
        ],
        listFn: (tid) => masterDataService.getSensorThresholds(tid),
        createFn: masterDataService.createSensorThreshold,
        updateFn: masterDataService.updateSensorThreshold,
        deleteFn: masterDataService.deleteSensorThreshold,
        idKey: "ruleId",
      },
      pmCompliance: {
        label: "PM Compliance",
        columns: [
          { key: "complianceId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "kpiName", label: "KPI" },
          { key: "targetValue", label: "Target" },
          { key: "periodType", label: "Period" },
        ],
        fields: [
          { name: "kpiName", label: "KPI Name", type: "text", required: true },
          {
            name: "targetValue",
            label: "Target Value",
            type: "number",
            step: "0.01",
            required: true,
          },
          {
            name: "periodType",
            label: "Period Type",
            type: "text",
            placeholder: "Monthly / Quarterly",
          },
        ],
        listFn: (tid) => masterDataService.getPmCompliance(tid),
        createFn: masterDataService.createPmCompliance,
        updateFn: masterDataService.updatePmCompliance,
        deleteFn: null,
        idKey: "complianceId",
      },

      workOrderPriorities: {
        label: "Work Order Priorities",
        columns: [
          { key: "priorityId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "priorityName", label: "Priority" },
          { key: "slaHours", label: "SLA (hrs)" },
        ],
        fields: [
          {
            name: "priorityName",
            label: "Priority Name",
            type: "text",
            required: true,
          },
          { name: "slaHours", label: "SLA Hours", type: "number" },
        ],
        listFn: (tid) => masterDataService.getWorkOrderPriorities(tid),
        createFn: masterDataService.createWorkOrderPriority,
        updateFn: masterDataService.updateWorkOrderPriority,
        deleteFn: null,
        idKey: "priorityId",
      },
      lockoutTagout: {
        label: "Lockout Tagout",
        columns: [
          { key: "lotoId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "templateName", label: "Template Name" },
        ],
        fields: [
          {
            name: "templateName",
            label: "Template Name",
            type: "text",
            required: true,
          },
          { name: "stepsJson", label: "Steps (JSON)", type: "textarea" },
        ],
        listFn: (tid) => masterDataService.getLockoutTagouts(tid),
        createFn: masterDataService.createLockoutTagout,
        updateFn: masterDataService.updateLockoutTagout,
        deleteFn: null,
        idKey: "lotoId",
      },
      sopDocuments: {
        label: "SOP Documents",
        columns: [
          { key: "docId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "title", label: "Title" },
          { key: "docType", label: "Type" },
          { key: "version", label: "Version" },
        ],
        fields: [
          { name: "title", label: "Title", type: "text", required: true },
          { name: "docType", label: "Doc Type", type: "text" },
          { name: "url", label: "URL / Path", type: "text" },
          { name: "version", label: "Version", type: "text" },
          { name: "validFrom", label: "Valid From", type: "date" },
          { name: "validTo", label: "Valid To", type: "date" },
        ],
        listFn: (tid) => masterDataService.getSopDocuments(tid),
        createFn: masterDataService.createSopDocument,
        updateFn: masterDataService.updateSopDocument,
        deleteFn: null,
        idKey: "docId",
      },
      holidayCalendar: {
        label: "Holiday Calendar",
        columns: [
          { key: "calendarId", label: "S.No", render: (v, item, index) => index + 1 },
          {
            key: "date",
            label: "Date",
            render: (v) => (v ? new Date(v).toLocaleDateString() : "-"),
          },
          { key: "description", label: "Description" },
          {
            key: "isWorkingDay",
            label: "Working Day",
            render: (v) => (v ? "Yes" : "No"),
          },
        ],
        fields: [
          { name: "date", label: "Date", type: "date", required: true },
          { name: "description", label: "Description", type: "textarea" },
          { name: "isWorkingDay", label: "Is Working Day", type: "checkbox" },
        ],
        listFn: (tid) => masterDataService.getHolidayCalendar(tid),
        createFn: masterDataService.createHolidayCalendar,
        updateFn: masterDataService.updateHolidayCalendar,
        deleteFn: null,
        idKey: "calendarId",
      },
      vendors: {
        label: "Vendors",
        columns: [
          { key: "vendorId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "vendorName", label: "Name" },
          { key: "contactInfo", label: "Contact" },
        ],
        fields: [
          {
            name: "vendorName",
            label: "Vendor Name",
            type: "text",
            required: true,
          },
          { name: "contactInfo", label: "Contact Info", type: "textarea" },
          { name: "leadTimeDays", label: "Lead Time (days)", type: "number" },
        ],
        listFn: (tid) => masterDataService.getVendors(tid),
        createFn: masterDataService.createVendor,
        updateFn: masterDataService.updateVendor,
        deleteFn: null,
        idKey: "vendorId",
      },
      maintenanceSla: {
        label: "Maintenance SLA",
        columns: [
          { key: "slaId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "priorityId", label: "Priority Id" },
          { key: "responseTimeHours", label: "Response (hrs)" },
          { key: "resolutionTimeHours", label: "Resolution (hrs)" },
        ],
        fields: [
          {
            name: "priorityId",
            label: "Priority Id",
            type: "number",
            required: true,
          },
          {
            name: "responseTimeHours",
            label: "Response Time (hrs)",
            type: "number",
          },
          {
            name: "resolutionTimeHours",
            label: "Resolution Time (hrs)",
            type: "number",
          },
        ],
        listFn: (tid) => masterDataService.getMaintenanceSla(tid),
        createFn: masterDataService.createMaintenanceSla,
        updateFn: masterDataService.updateMaintenanceSla,
        deleteFn: null,
        idKey: "slaId",
      },
      notificationTemplates: {
        label: "Notification Templates",
        columns: [
          { key: "templateId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "templateName", label: "Name" },
          { key: "channel", label: "Channel" },
        ],
        fields: [
          {
            name: "templateName",
            label: "Template Name",
            type: "text",
            required: true,
          },
          { name: "channel", label: "Channel", type: "text" },
          { name: "subject", label: "Subject", type: "text" },
          { name: "body", label: "Body", type: "textarea" },
        ],
        listFn: (tid) => masterDataService.getNotificationTemplates(tid),
        createFn: masterDataService.createNotificationTemplate,
        updateFn: masterDataService.updateNotificationTemplate,
        deleteFn: null,
        idKey: "templateId",
      },
      pmStandards: {
        label: "PM Standards",
        columns: [
          { key: "standardId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "standardCode", label: "Code" },
          { key: "standardName", label: "Name" },
          {
            key: "machineName",
            label: "Machine",
            render: (v, row) => row?.machineName || row?.MachineName || "-",
          },
          {
            key: "subassemblyName",
            label: "Subassembly",
            render: (v, row) => row?.subassemblyName || row?.SubassemblyName || "-",
          },
          {
            key: "isActive",
            label: "Active",
            render: (v) => (v ? "Yes" : "No"),
          },
        ],
        fields: [
          {
            name: "standardCode",
            label: "Standard Code",
            type: "text",
            required: true,
            placeholder: "e.g. PM-LUB-001"
          },
          {
            name: "standardName",
            label: "Standard Name",
            type: "text",
            required: true,
            placeholder: "Enter descriptive name"
          },
          {
            name: "machineId",
            label: "Machine",
            type: "select",
            required: true,
            optionsFn: (tid) =>
              machineService.getMachines(tid).then((r) =>
                r.map((x) => ({ value: x.machineId, label: `${x.machineCode} - ${x.machineName}` }))
              ),
          },
          {
            name: "subassemblyId",
            label: "Subassembly",
            type: "select",
            required: false,
            optionsFn: (tid, formData) => {
              const machineId = formData?.machineId;
              return subassemblyService.getAllSubassemblies(machineId).then((r) =>
                r.map((x) => ({ value: x.subassemblyId || x.id, label: `${x.subassemblyCode} - ${x.subassemblyName}` }))
              );
            }
          },
          { name: "isActive", label: "Active Status", type: "checkbox" },
          { name: "finding", label: "Finding", type: "text", placeholder: "Enter finding" },
          { name: "actionTaken", label: "Action Taken", type: "text", placeholder: "Enter action taken" },
        ],
        listFn: (tid) => masterDataService.getPMStandards(tid),
        createFn: masterDataService.createPMStandard,
        updateFn: masterDataService.updatePMStandard,
        deleteFn: masterDataService.deletePMStandard,
        idKey: "standardId",
      },
      machines: {
        label: "Machine Master",
        columns: [
          { key: "machineId", label: "S.No", render: (v, item, index) => index + 1 },
          { key: "machineCode", label: "Code" },
          { key: "machineName", label: "Name" },
          { key: "plantName", label: "Plant" },
          { key: "lineName", label: "Line" },
        ],
        fields: [
          {
            name: "machineCode",
            label: "Machine Code",
            type: "text",
            required: true,
            placeholder: "Enter machine code",
            fullWidth: true
          },
          {
            name: "machineName",
            label: "Machine Name",
            type: "text",
            required: true,
            placeholder: "Enter machine name",
            fullWidth: true
          },
          {
            name: "plantId",
            label: "Plant",
            type: "select",
            optionsFn: (tid) =>
              plantService.getAllPlants().then((r) =>
                r.map((x) => ({ value: x.plantId, label: x.plantName }))
              ),
          },
          {
            name: "lineId",
            label: "Line",
            type: "select",
            optionsFn: (tid, formData) =>
              lineService.getAllLines(formData?.plantId).then((r) =>
                r.map((x) => ({ value: x.lineId, label: x.lineName }))
              ),
          },
        ],
        listFn: () => machineService.getAllMachines(),
        createFn: machineService.createMachine,
        updateFn: machineService.updateMachine,
        deleteFn: machineService.deleteMachine,
        idKey: "machineId",
      },
    }),
    [],
  );

  const config = masterConfigs[selectedMaster];

  const [optionsMap, setOptionsMap] = useState({});
  const [optionsError, setOptionsError] = useState("");

  useEffect(() => {
    if (!tenantId) {
      setError("Please login to view data");
      setLoading(false);
      return;
    }
    loadItems();
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaster, tenantId]);

  const loadOptions = async (currentFormData = null) => {
    const selectFields = (config.fields || []).filter(
      (f) => f.type === "select" && typeof f.optionsFn === "function",
    );
    if (selectFields.length === 0) return;
    try {
      setOptionsError("");
      const entries = await Promise.all(
        selectFields.map(async (f) => {
          const opts = await f.optionsFn(tenantId, currentFormData || formData);
          return [f.name, opts || []];
        }),
      );
      setOptionsMap(Object.fromEntries(entries));
    } catch (err) {
      console.error("Options load error", err);
      setOptionsError("Some dropdowns failed to load. Please retry.");
    }
  };

  // Re-load options when machineId or other dependency changes (e.g. for PM Standards)
  useEffect(() => {
    if (showModal) {
      loadOptions(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.machineId, formData.lineId, formData.plantId]);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError("");
      let data = await config.listFn(tenantId);
      data = data || [];

      // Enrich items with machineName if needed
      const hasMachineCol = (config.columns || []).some(
        (c) => c.key === "machineName"
      );
      const needsMachineEnrich =
        hasMachineCol &&
        data.length > 0 &&
        !data[0].machineName &&
        data[0].machineId;

      if (needsMachineEnrich) {
        try {
          const machines = await machineService.getAllMachines();
          const machineMap = {};
          (machines || []).forEach((m) => {
            machineMap[m.machineId] = m.machineName;
          });
          data = data.map((item) => ({
            ...item,
            machineName:
              item.machineName || machineMap[item.machineId] || "-",
          }));
        } catch (e) {
          console.warn("Could not enrich machine names", e);
        }
      }

      setItems(data);
    } catch (err) {
      setError("Failed to load data. Please try again.");
      console.error("Load error", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    const initial = {};
    config.fields.forEach((f) => {
      if (f.type === "checkbox") {
        initial[f.name] = false;
      } else {
        initial[f.name] = "";
      }
    });

    // Handle single plant auto-selection if options are already loaded
    if (optionsMap.plantId?.length === 1) {
      initial.plantId = optionsMap.plantId[0].value.toString();
    }
    setFormData(initial);
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const next = {};
    config.fields.forEach((f) => {
      let val = item?.[f.name] ?? (f.type === "checkbox" ? false : "");
      if (f.name === "scheduleMode" && typeof val === "object") {
        val = val?.ScheduleMode || val?.scheduleMode || "Cron";
      }
      next[f.name] = val;
    });
    setFormData(next);
    setShowModal(true);
    setEditingItem(item);
  };

  const handleDelete = async (id) => {
    if (!config.deleteFn) {
      setDeleteConfirm(null);
      return;
    }
    try {
      setError("");
      await config.deleteFn(id);
      await loadItems();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError("");
      const payload = { tenantId, ...formData };
      if (config.idKey) {
        payload[config.idKey] = editingItem?.[config.idKey];
      }
      if (
        config.fields.some((f) => f.name === "startDate" && payload.startDate)
      ) {
        payload.startDate = new Date(payload.startDate);
      }
      if (config.fields.some((f) => f.name === "endDate" && payload.endDate)) {
        payload.endDate = new Date(payload.endDate);
      }
      if (
        config.fields.some(
          (f) => f.name === "nextDueDate" && payload.nextDueDate,
        )
      ) {
        payload.nextDueDate = new Date(payload.nextDueDate);
      }

      // Industry-standard sanitization for scheduleMode (to prevent 400 and React crashes)
      if (payload.scheduleMode) {
        if (typeof payload.scheduleMode === "object") {
          payload.scheduleMode =
            payload.scheduleMode?.ScheduleMode ||
            payload.scheduleMode?.scheduleMode ||
            "Cron";
        } else if (typeof payload.scheduleMode === "string") {
          payload.scheduleMode = payload.scheduleMode.trim() || "Cron";
        }
      } else {
        payload.scheduleMode = "Cron";
      }

      if (editingItem && config.updateFn) {
        await config.updateFn(editingItem[config.idKey], payload);
      } else if (config.createFn) {
        await config.createFn(payload);
      }
      await loadItems();
      setShowModal(false);
      setEditingItem(null);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to save. Please try again.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredItems = items.filter((item) =>
    Object.values(item || {})
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  if (loading) {
    return <PageLoading message="Loading master data..." />;
  }

  return (
    <div className="pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Planned Maintenance Masters</h2>
          <p>
            Manage maintenance plans, schedules, skills, tools, sensors, and
            related metadata
          </p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus className="btn-icon" />
          Add {config.label}
        </button>
      </div>

      <div className="selector-row">
        <label htmlFor="masterSelect">Select Master</label>
        <select
          id="masterSelect"
          value={selectedMaster}
          onChange={(e) => {
            setSelectedMaster(e.target.value);
            setSearchTerm("");
          }}
        >
          {Object.entries(masterConfigs).map(([key, cfg]) => (
            <option key={key} value={key}>
              {cfg.label}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredItems.length} {config.label}
          </div>
        </div>

        {filteredItems.length === 0 ? (
          <div className="empty-state">
            <h3>No records found</h3>
            <p>Use the New button to create a new record</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                {config.columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, index) => (
                <tr key={item[config.idKey]}>
                  {config.columns.map((col) => (
                    <td key={col.key}>
                      {col.render
                        ? col.render(item[col.key], item, index)
                        : (item[col.key] ?? "-")}
                    </td>
                  ))}
                  <td>
                    <div className="action-buttons">
                      {config.updateFn && (
                        <>
                          {selectedMaster !== "subassembly" && (
                            <button
                              className="btn-icon-view"
                              onClick={() => handleEdit(item)}
                              title="Add Findings"
                            >
                              <HiOutlineClipboardDocumentList />
                            </button>
                          )}
                          <button
                            className="btn-icon-edit"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                          >
                            <HiOutlinePencil />
                          </button>
                        </>
                      )}
                      {config.deleteFn && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(item)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingItem ? `Edit ${config.label}` : `New ${config.label}`}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowModal(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="pm-masters-form"
              className="btn-primary"
              disabled={submitting}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.625rem',
                background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                padding: '0.75rem 1.75rem',
                border: 'none',
                borderRadius: '12px',
                fontWeight: '600',
                fontSize: '0.95rem'
              }}
            >
              {submitting ? "Saving..." : (
                <>
                  <HiOutlineCheckCircle />
                  {editingItem ? "Update" : "Create"}
                </>
              )}
            </button>
          </>
        }
        customWidth="900px"
      >
        <div className="shared-modal-body">
          <form id="pm-masters-form" className="modal-form" onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              {config.fields
                .filter(f => !(!editingItem && (f.name === 'finding' || f.name === 'actionTaken')))
                .map((field) => (
                <div 
                  className="form-group" 
                  key={field.name}
                  style={{ 
                    gridColumn: field.type === 'textarea' || field.type === 'checkbox' || field.fullWidth ? 'span 2' : 'span 1',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem'
                  }}
                >
                  {field.type !== "checkbox" && (
                    <label htmlFor={field.name} style={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>
                      {field.label}{" "}
                      {field.required && <span className="required" style={{ color: 'var(--status-error)' }}>*</span>}
                    </label>
                  )}
                  
                  {field.type === "textarea" ? (
                    <textarea
                      id={field.name}
                      value={formData[field.name] ?? ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [field.name]: e.target.value,
                        })
                      }
                      placeholder={field.placeholder}
                      style={{ minHeight: '100px' }}
                    />
                  ) : field.type === "checkbox" ? (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      backgroundColor: '#f8fafc',
                      padding: '1.25rem',
                      borderRadius: '16px',
                      border: '1px solid #e2e8f0; border-color: var(--border-primary)',
                      marginTop: '0.5rem'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                          {field.label}
                        </span>
                        {field.hint && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{field.hint}</span>
                        )}
                      </div>
                      <label className="switch-pm">
                        <input
                          id={field.name}
                          type="checkbox"
                          checked={!!formData[field.name]}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              [field.name]: e.target.checked,
                            })
                          }
                        />
                        <span className="slider-pm"></span>
                      </label>
                    </div>
                  ) : field.type === "select" ? (
                    <select
                      id={field.name}
                      value={formData[field.name] ?? ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [field.name]: e.target.value,
                        })
                      }
                      required={field.required}
                    >
                      <option value="">Select {field.label}</option>
                      {(field.options || optionsMap[field.name] || []).map(
                        (opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ),
                      )}
                    </select>
                  ) : (
                    <input
                      id={field.name}
                      type={field.type || "text"}
                      value={formData[field.name] ?? ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [field.name]: e.target.value,
                        })
                      }
                      placeholder={field.placeholder}
                      step={field.step}
                      required={field.required}
                    />
                  )}
                  
                  {field.type !== 'checkbox' && field.hint && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                      {field.hint}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm[config.idKey])}
            >
              Delete
            </button>
          </>
        }
        customWidth="400px"
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete this record?
          </p>
          <p className="warning-text" style={{ color: 'var(--status-error)', marginTop: '0.5rem' }}>
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default PlannedMaintenanceMasters;
